import { getItemById } from '@shared/helpers/array.helpers';
import { copyBlueprintUnderlayToStoreyLevel } from '@shared/helpers/design/blueprint-underlay.helpers';
import { guards } from '@shared/helpers/typeguards.helpers';
import type { BlueprintUnderlay } from '@shared/models/design/blueprint-underlay.model';
import { resolveBlueprintRaster } from './blueprint-underlay-raster.cache';

/**
 * Which blueprint underlay rasters the current Design view needs.
 */
export type BlueprintUnderlayHydrationSelection = {
  /** Every underlay id on the design, in list order. */
  underlayIds: readonly string[];
  /** Page drawn on the map. Omitted when no underlay is active. */
  activeUnderlayId?: string;
  /** Pages whose detail is expanded and therefore shows a thumbnail. */
  openDetailUnderlayIds?: readonly string[];
};

/**
 * Underlay ids whose JPEG should be fetched for the current Design view.
 *
 * The map draws {@link BlueprintUnderlayHydrationSelection.activeUnderlayId}.
 * Open page details show a thumbnail. Other pages stay unloaded so opening a
 * project does not GET every underlay image.
 * Ids missing from {@link BlueprintUnderlayHydrationSelection.underlayIds}
 * are ignored. Result order follows `underlayIds`.
 */
export const selectBlueprintUnderlayIdsToHydrate = (
  selection: BlueprintUnderlayHydrationSelection,
): string[] => {
  const known = new Set(selection.underlayIds);
  const wanted = new Set<string>();
  if (selection.activeUnderlayId && known.has(selection.activeUnderlayId)) {
    wanted.add(selection.activeUnderlayId);
  }
  for (const id of selection.openDetailUnderlayIds ?? []) {
    if (known.has(id)) wanted.add(id);
  }
  return selection.underlayIds.filter((id) => wanted.has(id));
};

/**
 * True when copying this page must GET its JPEG first.
 *
 * A row with {@link BlueprintUnderlay.imageRef} blob-copies and must not GET.
 * A cached or inline raster is enough to upload the copy. Otherwise GET can
 * recover a blob that was never stored as `imageRef`.
 */
export const blueprintUnderlayCopyNeedsRasterFetch = (
  source: BlueprintUnderlay,
): boolean => {
  if (guards.is.nonEmptyString(source.imageRef)) return false;
  return !guards.is.nonEmptyString(
    resolveBlueprintRaster(source.id, source.imageDataUrl),
  );
};

/**
 * Underlay to duplicate.
 *
 * Keeps {@link BlueprintUnderlay.imageRef} when the live row has one, so
 * persist blob-copies instead of re-uploading. Otherwise attaches a cached
 * raster. Does not fetch; call {@link blueprintUnderlayCopyNeedsRasterFetch}
 * first when the cache may be empty.
 */
export const sourceBlueprintUnderlayForCopy = (
  source: BlueprintUnderlay,
): BlueprintUnderlay => {
  if (guards.is.nonEmptyString(source.imageRef)) return source;
  const imageDataUrl = resolveBlueprintRaster(source.id, source.imageDataUrl);
  if (
    !guards.is.nonEmptyString(imageDataUrl) ||
    imageDataUrl === source.imageDataUrl
  ) {
    return source;
  }
  return { ...source, imageDataUrl };
};

/**
 * Append a storey copy of `pageId`, or return `live` when that page is gone.
 *
 * Reads the live row so an `imageRef` written while this persist waited is kept.
 */
export const appendBlueprintUnderlayCopy = (
  live: readonly BlueprintUnderlay[],
  pageId: string,
  startLevel: number,
): readonly BlueprintUnderlay[] => {
  const source = getItemById(live, pageId, false);
  if (!source) return live;
  return [
    ...live,
    copyBlueprintUnderlayToStoreyLevel(
      sourceBlueprintUnderlayForCopy(source),
      startLevel,
    ),
  ];
};
